import dayjs from 'dayjs';
import { Component, Element, Event, EventEmitter, h, Listen, Prop, State, Watch } from '@stencil/core';
import {
  NextAvailableUpdateEvent,
  DateRange,
  SlotSearchRequest,
  NextAvailableDataUpdateEvent,
  logDebug,
  PractitionerSlotData,
  getNextDate,
  getReformattedDate,
  getSixMonthFutureDate,
  Option,
  DepartmentDaySlots,
  parseInput,
} from '@hpfc/fc-frontend-core';

/**
 * Modal component for showing more next available times specific to a doctor.
 */

@Component({
  tag: 'fccd-see-more-times-modal',
  styleUrl: 'see-more-times-modal.scss',
})
export class SeeMoreTimesModal {
  /**
   * This component
   */
  @Element()
  el: HTMLElement;

  /**
   * Show/hide the modal
   */
  @Prop()
  visible: boolean = false;

  /**
   * Header of modal
   */
  @Prop()
  header: string = '';

  /**
   * Doctor name, including credentials
   */
  @Prop({ mutable: true })
  doctorName: string = '';

  /**
   * Doctor specialty
   */
  @Prop({ mutable: true })
  specialty: string = '';

  /**
   * URL to the doctor image
   */
  @Prop({ mutable: true })
  doctorImage: string = '';

  /**
   * URL for the background image, to be displayed behind the doctor's headshot
   */
  @Prop()
  backgroundImage: string = '';

  /**
   * List of doctor's locations
   */
  @Prop({ mutable: true })
  locations: Array<Option> | string;

  /**
   * Next available times text, depending if times are available or not
   */
  @Prop()
  text: string = '';

  /**
   * State of the modal visibility
   */
  @State()
  isVisible: boolean = false;

  /**
   * State of the modal visibility
   */
  @State()
  practitionerId: string;

  @State()
  locationDaySlots: Array<DepartmentDaySlots>;

  @State()
  datePickerSelection: string;

  /**
   * The department id of the primary location being displayed
   */
  @State()
  primaryDepartmentId: number = 0;

  /**
   * The minimum number of days to show in the list.
   *
   * Currently set to a full month which disables the "See More Times" button.
   */
  @State()
  days: number = 31;

  @State()
  isLocationChange = false;

  readonly BODY_ID: string = `see-more-times-body-${crypto.randomUUID()}`;
  readonly DATE_PICKER_ID: string = `date-picker-${crypto.randomUUID()}`;
  readonly DATE_PICKER_TYPE: string = 'hp5DatepickerDateChangedEvent';
  readonly LOCATION_LIST_ID: string = `location-list-${crypto.randomUUID()}`;
  readonly SLOTS_PER_MONTH = 1800; // 30 days * 10 hours * 6 slots per hour
  /**
   * Div at the top of the modal, used for resetting the scroll position.
   */
  divElement: HTMLDivElement;

  /**
   * Event emitted when the user clicks on one of the components on the modal
   * which requires an update to the slots listed.
   */
  @Event()
  fccdNextAvailableUpdateEvent: EventEmitter<NextAvailableUpdateEvent>;

  /**
   * Watch for changes in the visibility triggered by the parent component.
   */
  @Watch('visible')
  handleVisibleChange(newValue: boolean) {
    this.isVisible = newValue;
  }

  /**
   * Event is emitted when a user closes or clicks way from the modal
   */
  @Listen('dismiss')
  @Listen('confirmed')
  @Listen('launchBridgeEvent')
  onModalDismiss() {
    this.isVisible = false;
    this.datePickerSelection = undefined;
    this.primaryDepartmentId = 0;
    this.divElement.scrollIntoView(false);
    this.practitionerId = undefined;
    // this.days = 10;
  }

  @Listen('fccdNextAvailableDataUpdateEvent', { target: 'document' })
  nextAvailableDataUpdate(event: CustomEvent<NextAvailableDataUpdateEvent>) {
    if (event.detail?.isError) {
      this.locationDaySlots = [];
      return;
    }

    if (!event?.detail?.data) {
      return;
    }

    logDebug('Control: ' + event.detail.data);
    const data: PractitionerSlotData = event.detail.data;
    this.locationDaySlots = data.departmentDaysSlots;

    if (this.getDatePickerValue() === '' || this.getDatePickerValue() == null) {
      const firstSlotDate = new Date(this.getEarliestDateTimeSlot(this.locationDaySlots));
      this.setDatePickerValue(firstSlotDate);
      this.isLocationChange = false;
    }

    //move these out
    this.doctorName = data.practitionerName;
    this.doctorImage = data.practitionerImage;
    this.specialty = data.specialty;
    if (this.practitionerId != data.practitionerId) {
      this.practitionerId = data.practitionerId;
      this.locations = data.locations;
    }

    // On fresh load of modal we set the primary department based on the
    // initial query made for slots
    if (this.primaryDepartmentId === 0) {
      this.primaryDepartmentId = parseInt(data.slotData.departmentId);
    }

    this.isVisible = true;
  }

  private getEarliestDateTimeSlot(daySlots: Array<DepartmentDaySlots>) {
    // Find the earliest day slot group
    const locationDaySlot = daySlots.reduce((earliest, slot) => {
      return dayjs(slot.sortDate).isBefore(dayjs(earliest.sortDate)) ? slot : earliest;
    });

    // Find the earliest slot in the day slot group
    const firstSlotStr = locationDaySlot.dateTimes.reduce((earliest, slot) => {
      return dayjs(slot).isBefore(dayjs(earliest)) ? slot : earliest;
    });
    return firstSlotStr;
  }

  /**
   * Event emitted when a component's value changes.
   *
   * This handler includes both the date picker, and the location list.
   */
  @Listen('hp5ValidationChange')
  onValidationChange(event: CustomEvent) {
    if (event?.detail?.validationState === 'SUCCESS') {
      this.handleValidationChange(event);
    }
  }

  /**
   * Event emitted when the date picker loses focus
   */
  @Listen('hp5ValidationBlur')
  onValidationBlur(event: CustomEvent) {
    if (event?.detail?.originalEvent?.srcElement?.id === this.DATE_PICKER_ID) {
      this.handleDatePickerBlur(event?.detail?.validationState);
    }
  }

  /**
   * Set the visibility to match when the component loads, watch will not
   * catch.
   */
  componentWillLoad() {
    this.isVisible = this.visible;
  }

  /**
   * Load more times in the modal
   */
  private seeMoreTimes = () => {
    // this.days = this.days + 10;
  };

  private get locationsArray(): Array<Option> {
    return parseInput(this.locations);
  }

  private getSlotSearch(selectedDate: string) {
    const slotSearchRequest: SlotSearchRequest = new SlotSearchRequest();

    this.assignDepartmentIds(slotSearchRequest);

    this.assignDateRange(selectedDate, slotSearchRequest);
    this.datePickerSelection = selectedDate;
    slotSearchRequest.practitionerIds = [this.practitionerId];

    slotSearchRequest.slotLimit = this.SLOTS_PER_MONTH;
    return slotSearchRequest;
  }

  /**
   * Assign the date range to the slotSearchRequest, using the selected date, or the next 30 days.
   * @param selectedDate  The selected date
   * @param slotSearchRequest  SlotSearchRequest to set the date range
   */
  private assignDateRange(selectedDate: string, slotSearchRequest: SlotSearchRequest) {
    if (selectedDate) {
      const maxDate = dayjs().add(6, 'months');
      const dateTo = getNextDate(selectedDate, 30, maxDate);
      const dateFrom = getReformattedDate(selectedDate);
      slotSearchRequest.dateRange = new DateRange(dateFrom, dateTo);
    } else {
      slotSearchRequest.dateRange = DateRange.next30Days();
    }
  }

  /**
   * Assign the departmentIds to the slotSearchRequest, using the selected location, or the first element in the list.
   * @param slotSearchRequest  SlotSearchRequest to set the departmentIds
   */
  private assignDepartmentIds(slotSearchRequest: SlotSearchRequest) {
    const locationListElement = this.el.querySelector(`#${this.LOCATION_LIST_ID}`) as HTMLSelectElement;
    const location: string = locationListElement ? locationListElement.value : undefined;
    if (location) {
      slotSearchRequest.departmentIds = [parseInt(location)];
    } else {
      const locationsList: Array<Option> = this.locationsArray;
      if (locationsList && locationsList.length > 0) {
        slotSearchRequest.departmentIds = [parseInt(locationsList[0].value)];
      }
    }
    return slotSearchRequest;
  }

  private getSelectedDepartmentId(): string {
    const locationListElement = this.el.querySelector(`#${this.LOCATION_LIST_ID}`) as HTMLSelectElement;
    return locationListElement?.value;
  }

  handleDatePickerBlur(validationState: 'SUCCESS' | 'ERROR' | undefined) {
    const selection = this.getDatePickerValue();
    const isNewDateSelectionEmpty = selection === '';
    const isExistingDateSelected = this.datePickerSelection !== undefined;
    const isValidationError = validationState === 'ERROR';
    if (isValidationError && isNewDateSelectionEmpty && isExistingDateSelected) {
      const newEvent = new NextAvailableUpdateEvent(this.getSlotSearch(undefined), false);
      this.fccdNextAvailableUpdateEvent.emit(newEvent);
    }
  }

  handleValidationChange(event: CustomEvent) {
    const originalEvent = event?.detail?.originalEvent;

    // DATE_PICKER_TYPE corresponds to events fired by choosing a date from the visual date picker
    // DATE_PICKER_ID corresponds to events fired by manually typing in a date
    const isDatePickerEvent = originalEvent?.type === this.DATE_PICKER_TYPE || originalEvent?.target?.parentElement?.elementId === this.DATE_PICKER_ID;
    const isLocationListEvent = originalEvent?.srcElement?.id === this.LOCATION_LIST_ID;

    // Date picker selection changed
    if (isDatePickerEvent && event?.detail?.value !== this.datePickerSelection) {
      this.isLocationChange = false;
      const newEvent = new NextAvailableUpdateEvent(this.getSlotSearch(event.detail?.value), false);
      this.fccdNextAvailableUpdateEvent.emit(newEvent);
    }

    // Location list selection changed
    if (isLocationListEvent) {
      this.isLocationChange = true;
      // Set the value of getSelectedDepartmentId() to the selected this.locationsArray
      if (this.locationsArray && this.locationsArray.length > 0) {
        this.locationsArray.forEach(location => {
          location.isSelected = location.value === this.getSelectedDepartmentId().toString();
        });
      }

      const newEvent = new NextAvailableUpdateEvent(this.getSlotSearch(this.datePickerSelection), false);
      this.fccdNextAvailableUpdateEvent.emit(newEvent);
    }
  }

  getDatePickerValue() {
    const datePicker = this.el.querySelector(`#${this.DATE_PICKER_ID}`) as HTMLInputElement;
    return datePicker.value;
  }

  setDatePickerValue(date: Date) {
    this.datePickerSelection = dayjs(date).format('MM/DD/YYYY');
  }

  render() {
    const { isVisible, header, doctorName, specialty, doctorImage, backgroundImage, locationsArray, text, days, locationDaySlots } = this;

    // location is either array of objects or a string of json
    const locationsList: Array<Option> = isVisible ? locationsArray : [];
    const selectKey = locationsList?.map(location => location.value).join('-') || 'selectKey';
    const selected = locationsList?.find(loc => loc.isSelected) || locationsList?.[0];
    const locationName = selected?.name;

    const body = (
      <div class="body" id={this.BODY_ID}>
        <div class="doctor-info">
          <hp5-person-image src={doctorImage} background-image={backgroundImage} doctor="true" size-selection="sm" />
          <hp5-heading size-selection="md" tag-selection="h2">
            {doctorName}
            <hp5-semantic size-selection="sm">{specialty}</hp5-semantic>
          </hp5-heading>
        </div>
        <hp5-container class="location-and-date" columns-xs="1" columns-sm="2">
          {locationsArray && locationsArray.length > 1 ? <hp5-select key={selectKey} element-id={this.LOCATION_LIST_ID} label="Location" options={locationsArray} /> : null}
          <hp5-input-datepicker
            disable-dates-all-past="true"
            disable-dates-all-after={getSixMonthFutureDate()}
            element-id={this.DATE_PICKER_ID}
            input-help-text="Example: mm/dd/yyyy"
            label="Date"
            popup-boundary-id={this.BODY_ID}
            selected-date={this.datePickerSelection}
            initial-value={this.datePickerSelection}
          />
        </hp5-container>
        <hp5-horizontal-rule />
        <hp5-spacing-vertical xs="sm" />
        <div class="next-available" aria-live="polite">
          {text ? (
            <div>
              <hp5-heading size-selection="sm">{text}</hp5-heading>
              <hp5-spacing-vertical xs="sm" />
            </div>
          ) : null}
          <div class="time-slots">
            <fccd-time-slot-grid department-day-slots={JSON.stringify(locationDaySlots)} number-of-days-to-show={days} location-name={locationName}></fccd-time-slot-grid>
            <hp5-spacing-vertical xs="sm" sm="sm" md="sm" lg="sm" />
            {days < 30 ? (
              <div class="load-more-times">
                <hp5-horizontal-rule />
                <hp5-button-decorator type="tertiary">
                  <button type="button" onClick={() => this.seeMoreTimes()}>
                    <hp5-icon name="system-show" size="xs" />
                    See more times
                  </button>
                </hp5-button-decorator>
                <hp5-spacing-vertical xs="sm" sm="sm" md="sm" lg="sm" />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );

    return (
      <hp5-modal-basic cancel-button-text="Cancel" confirm-button-hidden="true" scrolling="fixed" title-text={header} visible={isVisible} always-dismiss="true" narrow={true}>
        <div ref={el => (this.divElement = el)} />
        {body}
      </hp5-modal-basic>
    );
  }
}
